import {
  Grid,
  Loader,
  Stack,
} from '@mantine/core'

import { useForm } from '@mantine/form'
import {
  useEffect,
  useMemo,
} from 'react'

import Device, { Timezone } from '@data/device/model/device'

import { CURRENCY_OPTIONS } from '@components/main/DeviceManagement/DeviceInformationManager/constants'
import { ReadOnlyField } from '@components/ui/form/ReadOnlyField'
import { DirtyTextInput } from '@components/ui/form/DirtyTextInput'
import { DirtyNumberInput } from '@components/ui/form/DirtyNumberInput'
import { DirtySelect } from '@components/ui/form/DirtySelect'
import { UpdateButton } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/UpdateButton'

export interface DeviceInfoTabProps {
  device: Device
  timezones: Timezone[]
  timezonesLoading: boolean
  timezonesError: boolean
  deviceAdmin: boolean
  userUpdateFields: boolean
  deviceUpdating: boolean
  onUpdate: (device: Device) => Promise<void>
  onUpdatePulses: (
    pulses: number,
  ) => Promise<void>
}

/**
 * Maps Device -> Form State
 */
const mapDeviceToForm = (
  device: Device,
) => ({
  deviceName: device.deviceName,
  location: device.deviceSettings.location || '',
  currency: device.deviceSettings.currency,
  cost: device.deviceSettings.cost,
  timezone: device.deviceSettings.timezone,
  occupants: device.deviceSettings.occupants,
  co2Rate: device.deviceSettings.co2Rate,
  clientMeterId: device.deviceSettings.clientMeterId,
})


export const DeviceInfoTab = ({
                                device,
                                timezones,
                                timezonesLoading,
                                timezonesError,
                                deviceAdmin,
                                userUpdateFields,
                                deviceUpdating,
                                onUpdate,
                              }: DeviceInfoTabProps) => {
  const form = useForm({
    initialValues:
      mapDeviceToForm(device),

    validate: {
      deviceName: (value) =>
        value.length === 0
          ? 'Required'
          : null,

      currency: (value) =>
        value.length !== 3
          ? 'Must be 3 chars'
          : null,
    },
  })

  // Mantine's Select renders an empty field when `value` is absent from `data`, so the
  // device's own timezone is seeded in while the list is still loading — and stays seeded
  // if the endpoint ever stops returning it, which would otherwise blank the field and
  // lose the value on the next save.
  const timezoneOptions = useMemo(
    () => {
      const current = device.deviceSettings.timezone
      const options = timezones.map(
        (t) => ({
          value: t.name,
          label: t.name,
        }),
      )

      return current && !timezones.some((t) => t.name === current)
        ? [{ value: current, label: current }, ...options]
        : options
    },
    [timezones, device.deviceSettings.timezone],
  )

  /**
   * Sync latest server state into form
   * and reset dirty tracking.
   */
  useEffect(() => {
    if (!device) return

    const nextValues =
      mapDeviceToForm(device)

    form.setInitialValues(nextValues)
    form.setValues(nextValues)
    form.resetDirty(nextValues)
  }, [device])

  const handleSubmit = async (
    values: typeof form.values,
  ) => {
    const updatedDevice: Device = {
      ...device,
      deviceName: values.deviceName,
      deviceSettings: {
        ...device.deviceSettings,
        deviceName: values.deviceName,
        location: values.location,
        currency: values.currency,
        cost: values.cost,
        timezone: values.timezone,
        occupants: values.occupants,
        co2Rate: values.co2Rate,
        clientMeterId:
        values.clientMeterId,
      },
    }

    await onUpdate(updatedDevice)

    /**
     * Safety reset in case parent
     * refresh is delayed slightly.
     */
    form.resetDirty()
  }

  return (
    <form
      onSubmit={form.onSubmit(
        handleSubmit,
      )}
    >
      <Stack gap="lg">
        <Grid>
          <Grid.Col span={{ base: 12 }}>
            <DirtyTextInput
              form={form}
              field="deviceName"
              label="Device Name"
              disabled={!deviceAdmin}
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtyTextInput
              form={form}
              field="location"
              label="Location Reference"
              disabled={!deviceAdmin}
              tooltip='A short description of where the device is installed to help engineers/clients locate it on future visits.'
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ReadOnlyField
              label="ID"
              value={device.deviceId}
              enableClipboard
              tooltip='This is the ID of the physical SMARTFLOW device.'
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ReadOnlyField
              label="UUID"
              value={device.dUuid}
              enableClipboard
              tooltip='This is the ID is used for internal systems only.'
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <ReadOnlyField
              label="Link ID"
              value={String(device.dlId)}
              enableClipboard
              tooltip='This is the unique ID used to identify this device asset. This ID does not change throughout the life time of the asset.'
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtyNumberInput
              form={form}
              field="cost"
              label="Cost"
              min={0}
              decimalScale={2}
              disabled={!deviceAdmin && !userUpdateFields}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtySelect
              form={form}
              field='currency'
              label="Currency"
              data={CURRENCY_OPTIONS}
              disabled={!deviceAdmin && !userUpdateFields}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtySelect
              form={form}
              field='timezone'
              label='Timezone'
              data={timezoneOptions}
              disabled={!deviceAdmin || timezonesLoading || timezonesError}
              rightSection={timezonesLoading ? <Loader size={'xs'} /> : undefined}
              error={timezonesError ? 'Could not load timezones' : undefined}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtyNumberInput
              form={form}
              field='occupants'
              label='Occupants'
              min={0}
              disabled={!deviceAdmin && !userUpdateFields}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtyNumberInput
              form={form}
              field='co2Rate'
              label='CO2 Rate'
              min={0}
              decimalScale={2}
              disabled={!deviceAdmin && !userUpdateFields}
            />
          </Grid.Col>

          <Grid.Col
            span={{ base: 12, md: 6 }}
          >
            <DirtyTextInput
              form={form}
              field='clientMeterId'
              label='Client Meter ID'
              disabled={!deviceAdmin && !userUpdateFields}
            />
          </Grid.Col>
        </Grid>

        <UpdateButton
          type='submit'
          buttonText='Update Info'
          loading={deviceUpdating}
          disabled={!form.isDirty()}
        />
      </Stack>
    </form>
  )
}
